import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// hooks
import { usePrevious } from 'helpers/hooks/usePrevious';

// MUI components
import { IconButton, CircularProgress } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { Theme } from 'assets/types';

// components
import MentionsTextField from 'components/MentionsTextField';
import { isOverCharacterLimit } from 'components/MentionsTextField/InfoSection';

// action creators
import { createCommentAction } from 'store/actions/commentActions';

// selectors
import getLoadingFromState from 'store/selectors/getLoadingFromState';
import getStatusFromState from 'store/selectors/getStatusFromState';

// helpers
import { MAX_LENGTH } from './Comments';

type Props = {
  activityId: number;
  onCreate: (...args: any[]) => any;
};

export default function CommentInput({ activityId, onCreate }: Props) {
  const classes = useStyles();
  const [body, setBody] = useState('');
  const dispatch = useDispatch();
  const metaId = `createComment-${activityId}`;
  const loading = useSelector(getLoadingFromState('comment', metaId, false));
  const prevLoading = usePrevious(loading);
  const status = useSelector(getStatusFromState('comment', metaId, 'success'));

  const isOverLimit = isOverCharacterLimit(body, MAX_LENGTH);
  const isSendDisabled = loading || !body.trim() || isOverLimit;

  useEffect(() => {
    if (prevLoading && !loading && status === 'success') {
      setBody('');
      onCreate();
    }
  }, [status, prevLoading, loading, onCreate]);

  const handleChange = (_: unknown, newValue: string) => setBody(newValue);
  const createComment = () => {
    if (isSendDisabled) return;

    dispatch(createCommentAction({ fields: { activityId, body }, metaId }));
  };
  const handleCtrlEnter = (event: any) => {
    if (
      !isSendDisabled &&
      (event.ctrlKey || event.metaKey) &&
      event.key === 'Enter'
    )
      createComment();
  };
  return (
    <MentionsTextField
      label='Comment'
      maxLength={MAX_LENGTH}
      aria-label='Leave a comment'
      placeholder='Leave a comment...'
      value={body}
      onChange={handleChange}
      onKeyDown={handleCtrlEnter}
      containerClassName={classes.background}
      endAdornment={
        <IconButton
          onClick={createComment}
          disabled={isSendDisabled}
          size='small'
          aria-label='Post comment'
        >
          {loading ? <CircularProgress size={24} /> : <Send fontSize='small' />}
        </IconButton>
      }
    />
  );
}
const useStyles = makeStyles((theme: Theme) => ({
  background: {
    backgroundColor: theme.palette.background.lightPaper,
    borderRadius: '4px',
    padding: '10px 8px',
  },
}));
